import React, { useEffect, useMemo, useRef } from "react";
import { observer } from "mobx-react";
import { CreditTierViewStore } from "../../store";
import { CreditTierView } from "../view/CreditTierView";
import { CreditTierUpdateStore } from "../../store";
import { CreditTierUpdate } from "../update/CreditTierUpdate";
import { useRootStore } from "../../../../store/contexts/RootStoreContext";
import { Box, CircularProgress } from "@mui/material";

export type CreditTierModalContentProps = {
  entityId: string;
  mode: "view" | "update";
  onClose: () => void;
};

export const CreditTierModalContent: React.FC<CreditTierModalContentProps> =
  observer(({ entityId, mode, onClose }) => {
    const rootStore = useRootStore();
    const initializedRef = useRef<string | null>(null);

    const storeInfo = useMemo(() => {
      if (mode === "update") {
        const s = new CreditTierUpdateStore(rootStore);
        s.isModalMode = true;
        return { store: s, type: "update" as const };
      }
      // Default to view mode
      const s = new CreditTierViewStore(rootStore);
      s.isModalMode = true;
      return { store: s, type: "view" as const };
    }, [rootStore, mode]);

    useEffect(() => {
      if (entityId && initializedRef.current !== entityId) {
        initializedRef.current = entityId;
        storeInfo.store.initialize({ id: entityId });
      }
    }, [entityId, storeInfo]);

    useEffect(() => {
      return () => {
        storeInfo.store.destroy();
      };
    }, [storeInfo]);

    const store = storeInfo.store;
    if (store.status === "processing" || store.status === "uninitialized") {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {storeInfo.type === "view" && (
          <CreditTierView
            creditTierViewStore={storeInfo.store as CreditTierViewStore}
          />
        )}
        {storeInfo.type === "update" && (
          <CreditTierUpdate
            creditTierUpdateStore={storeInfo.store as CreditTierUpdateStore}
            isModalMode
            onModalClose={onClose}
          />
        )}
      </Box>
    );
  });
